import { Controller } from "@hotwired/stimulus"
import { WebRTCAdaptor } from "@antmedia/webrtc_adaptor"

/**
 * Live Broadcaster Controller
 *
 * Handles WebRTC streaming to Ant Media Server for live match broadcasts.
 *
 * Usage:
 * <div data-controller="live-broadcaster"
 *      data-live-broadcaster-ant-media-url-value="wss://live.mipadel.net/mipadel/websocket"
 *      data-live-broadcaster-api-url-value="/api/live_streams"
 *      data-live-broadcaster-match-id-value="123">
 */
export default class extends Controller {
  static targets = [
    "preview", "startBtn", "stopBtn", "timer", "status",
    "viewerCount", "muteBtn", "flipBtn", "shareUrl", "errorMsg"
  ]

  static values = {
    matchId: { type: Number, default: 0 },
    antMediaUrl: String,
    apiUrl: { type: String, default: "/api/live_streams" }
  }

  connect() {
    this.webRTCAdaptor = null
    this.stream = null
    this.streamId = null
    this.liveStreamId = null  // Rails model ID
    this.publishToken = null
    this.timerInterval = null
    this.startTime = null
    this.isLive = false
    this.isMuted = false
    this.facingMode = "environment" // Caméra arrière par défaut

    // Wake Lock pour éviter la mise en veille
    this.wakeLock = null

    // Check for WebRTC support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this._showError("Votre navigateur ne supporte pas la diffusion vidéo")
      if (this.hasStartBtnTarget) this.startBtnTarget.disabled = true
    }
  }

  disconnect() {
    this._cleanup()
  }

  async startLive() {
    try {
      this._clearError()
      this.statusTarget.textContent = "Préparation du stream..."
      this.startBtnTarget.disabled = true

      // 1. Demander accès caméra
      await this._initializeCamera()

      // 2. Créer le stream sur le serveur
      await this._createStreamOnServer()

      // 3. Initialiser WebRTC Adaptor
      await this._initializeWebRTC()

      // 4. Activer Wake Lock
      await this._requestWakeLock()

      // 5. Publier le stream
      this._publish()

    } catch (error) {
      console.error("Error starting live:", error)
      this._showError(error.message || "Erreur lors du démarrage")
      this.startBtnTarget.disabled = false
      this._cleanup()
    }
  }

  async stopLive() {
    if (!this.isLive) return

    try {
      this.statusTarget.textContent = "Arrêt du stream..."
      this.stopBtnTarget.disabled = true

      // Arrêter la publication WebRTC
      if (this.webRTCAdaptor) {
        this.webRTCAdaptor.stop(this.streamId)
      }

      // Notifier le serveur
      await this._stopStreamOnServer()

      this._cleanup()

      this.statusTarget.textContent = "Stream terminé"
      this.startBtnTarget.disabled = false
      this.startBtnTarget.classList.remove("hidden")
      this.stopBtnTarget.classList.add("hidden")

    } catch (error) {
      console.error("Error stopping live:", error)
      this._showError("Erreur lors de l'arrêt")
    }
  }

  toggleMute() {
    if (!this.stream) return

    this.isMuted = !this.isMuted
    this.stream.getAudioTracks().forEach(track => {
      track.enabled = !this.isMuted
    })

    if (this.hasMuteBtnTarget) {
      this.muteBtnTarget.classList.toggle("bg-red-600", this.isMuted)
      this.muteBtnTarget.classList.toggle("bg-gray-700", !this.isMuted)
      this.muteBtnTarget.setAttribute("title", this.isMuted ? "Activer le son" : "Couper le son")
    }
  }

  async flipCamera() {
    if (!this.stream) return

    try {
      // Alterner entre caméra avant/arrière
      this.facingMode = this.facingMode === "environment" ? "user" : "environment"

      // Arrêter les tracks actuels
      this.stream.getTracks().forEach(track => track.stop())

      // Réinitialiser avec nouvelle caméra
      await this._initializeCamera()

      // Mettre à jour le stream WebRTC si en cours de diffusion
      if (this.webRTCAdaptor && this.isLive) {
        const videoTrack = this.stream.getVideoTracks()[0]
        if (videoTrack) {
          this.webRTCAdaptor.switchVideoCapture(this.streamId, videoTrack)
        }
      }
    } catch (error) {
      console.error("Error flipping camera:", error)
      this._showError("Impossible de changer de caméra")
    }
  }

  copyShareUrl() {
    if (this.hasShareUrlTarget && this.shareUrlTarget.value) {
      navigator.clipboard.writeText(this.shareUrlTarget.value)
        .then(() => {
          // Brief visual feedback
          const originalText = this.shareUrlTarget.nextElementSibling?.textContent
          if (this.shareUrlTarget.nextElementSibling) {
            this.shareUrlTarget.nextElementSibling.textContent = "Copié !"
            setTimeout(() => {
              this.shareUrlTarget.nextElementSibling.textContent = originalText
            }, 2000)
          }
        })
        .catch(err => console.error("Copy failed:", err))
    }
  }

  // Private methods

  async _initializeCamera() {
    const constraints = {
      video: {
        facingMode: this.facingMode,
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 30, max: 30 }
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints)
    } catch (error) {
      if (error.name === "NotAllowedError") {
        throw new Error("Accès à la caméra refusé. Veuillez autoriser l'accès.")
      } else if (error.name === "NotFoundError") {
        throw new Error("Aucune caméra détectée sur cet appareil.")
      } else {
        throw new Error("Impossible d'accéder à la caméra: " + error.message)
      }
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.srcObject = this.stream
    }
  }

  async _createStreamOnServer() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const body = {}
    if (this.matchIdValue && this.matchIdValue > 0) {
      body.match_id = this.matchIdValue
    }

    const response = await fetch(this.apiUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      let errorMsg = "Impossible de créer le stream"
      try {
        const error = await response.json()
        errorMsg = error.error || errorMsg
      } catch (e) {
        // Ignore JSON parse errors
      }
      throw new Error(errorMsg)
    }

    const data = await response.json()
    this.streamId = data.stream_id
    this.liveStreamId = data.id
    this.publishToken = data.publish_token
    this.hlsUrl = data.hls_url

    // Mettre à jour l'URL de partage
    if (this.hasShareUrlTarget) {
      const shareUrl = `${window.location.origin}/live/${this.streamId}`
      this.shareUrlTarget.value = shareUrl
    }
  }

  _initializeWebRTC() {
    return new Promise((resolve, reject) => {
      const websocketUrl = this.antMediaUrlValue

      if (!websocketUrl) {
        reject(new Error("URL WebSocket Ant Media non configurée"))
        return
      }

      this.webRTCAdaptor = new WebRTCAdaptor({
        websocket_url: websocketUrl,
        mediaConstraints: {
          video: true,
          audio: true
        },
        localVideoId: this.hasPreviewTarget ? this.previewTarget.id : null,
        sdp_constraints: {
          OfferToReceiveAudio: false,
          OfferToReceiveVideo: false
        },
        bandwidth: 2500, // 2.5 Mbps
        callback: (info, obj) => {
          this._handleWebRTCCallback(info, obj, resolve, reject)
        },
        callbackError: (error, message) => {
          console.error("WebRTC Error:", error, message)
          if (!this.isLive) {
            reject(new Error(message || error))
          } else {
            this._showError("Erreur WebRTC: " + (message || error))
          }
        }
      })
    })
  }

  _handleWebRTCCallback(info, obj, resolve, reject) {
    console.log("WebRTC callback:", info, obj)

    switch (info) {
      case "initialized":
        console.log("WebRTC Adaptor initialized")
        resolve()
        break

      case "publish_started":
        console.log("Stream published:", this.streamId)
        this.isLive = true
        this._onLiveStarted()
        break

      case "publish_finished":
        console.log("Stream publish finished")
        this.isLive = false
        break

      case "closed":
        console.log("WebSocket closed")
        if (this.isLive) {
          this._handleDisconnection()
        }
        break

      case "ice_connection_state_changed":
        console.log("ICE state:", obj?.state)
        if (obj?.state === "failed" || obj?.state === "disconnected") {
          this._handleDisconnection()
        }
        break

      case "bitrateMeasurement":
        // Could update a bitrate indicator
        break
    }
  }

  _publish() {
    if (!this.webRTCAdaptor || !this.streamId) {
      throw new Error("WebRTC non initialisé")
    }

    // Utiliser notre stream local
    this.webRTCAdaptor.localStream = this.stream

    // Publier avec token
    this.webRTCAdaptor.publish(
      this.streamId,
      this.publishToken,
      null,  // subscriberId
      null,  // subscriberCode
      null,  // streamName
      null,  // mainTrack
      null   // metaData
    )
  }

  _onLiveStarted() {
    this.statusTarget.innerHTML = '<span class="inline-flex items-center"><span class="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-2"></span>LIVE</span>'
    this.startBtnTarget.classList.add("hidden")
    this.stopBtnTarget.classList.remove("hidden")
    this.stopBtnTarget.disabled = false

    if (this.hasMuteBtnTarget) this.muteBtnTarget.classList.remove("hidden")
    if (this.hasFlipBtnTarget) this.flipBtnTarget.classList.remove("hidden")

    this._startTimer()
    this._startViewerPolling()
  }

  _handleDisconnection() {
    this.statusTarget.textContent = "Connexion perdue, reconnexion..."

    // Tenter une reconnexion après 2 secondes
    setTimeout(async () => {
      if (!this.isLive && this.streamId) {
        try {
          await this._initializeWebRTC()
          this._publish()
        } catch (error) {
          console.error("Reconnection failed:", error)
          this._showError("Reconnexion échouée")
        }
      }
    }, 2000)
  }

  _startTimer() {
    this.startTime = Date.now()
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000)
      const hours = String(Math.floor(elapsed / 3600)).padStart(2, "0")
      const minutes = String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0")
      const seconds = String(elapsed % 60).padStart(2, "0")
      if (this.hasTimerTarget) {
        this.timerTarget.textContent = `${hours}:${minutes}:${seconds}`
      }
    }, 1000)
  }

  _startViewerPolling() {
    // Polling pour le nombre de viewers toutes les 5 secondes
    this.viewerInterval = setInterval(async () => {
      if (!this.liveStreamId) return

      try {
        const response = await fetch(`${this.apiUrlValue}/${this.liveStreamId}`)
        if (response.ok) {
          const data = await response.json()
          if (this.hasViewerCountTarget) {
            const count = data.viewer_count || 0
            this.viewerCountTarget.textContent = `${count} viewer${count !== 1 ? 's' : ''}`
          }
        }
      } catch (e) {
        // Ignore polling errors silently
      }
    }, 5000)
  }

  async _stopStreamOnServer() {
    if (!this.liveStreamId) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(`${this.apiUrlValue}/${this.liveStreamId}/stop`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken }
      })
    } catch (error) {
      console.error("Error stopping stream on server:", error)
    }
  }

  async _requestWakeLock() {
    if ('wakeLock' in navigator) {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen')
        console.log("Wake Lock activé")

        // Réactiver si l'écran revient visible
        document.addEventListener('visibilitychange', this._handleVisibilityChange.bind(this))
      } catch (e) {
        console.warn("Wake Lock non disponible:", e)
      }
    }
  }

  async _handleVisibilityChange() {
    if (document.visibilityState === 'visible' && this.isLive && !this.wakeLock) {
      await this._requestWakeLock()
    }
  }

  _releaseWakeLock() {
    if (this.wakeLock) {
      this.wakeLock.release()
      this.wakeLock = null
    }
    document.removeEventListener('visibilitychange', this._handleVisibilityChange)
  }

  _showError(message) {
    if (this.hasErrorMsgTarget) {
      this.errorMsgTarget.textContent = message
      this.errorMsgTarget.classList.remove("hidden")
    } else {
      console.error("Broadcast error:", message)
    }
  }

  _clearError() {
    if (this.hasErrorMsgTarget) {
      this.errorMsgTarget.textContent = ""
      this.errorMsgTarget.classList.add("hidden")
    }
  }

  _cleanup() {
    // Stop timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }

    // Stop viewer polling
    if (this.viewerInterval) {
      clearInterval(this.viewerInterval)
      this.viewerInterval = null
    }

    // Stop media stream
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    // Close WebRTC
    if (this.webRTCAdaptor) {
      try {
        this.webRTCAdaptor.closeWebSocket()
      } catch (e) {
        // Ignore close errors
      }
      this.webRTCAdaptor = null
    }

    // Release wake lock
    this._releaseWakeLock()

    // Clear preview
    if (this.hasPreviewTarget) {
      this.previewTarget.srcObject = null
    }

    // Reset state
    this.isLive = false
    this.streamId = null
    this.liveStreamId = null
    this.publishToken = null

    // Reset UI
    if (this.hasTimerTarget) this.timerTarget.textContent = "00:00:00"
    if (this.hasViewerCountTarget) this.viewerCountTarget.textContent = "0 viewers"
    if (this.hasMuteBtnTarget) this.muteBtnTarget.classList.add("hidden")
    if (this.hasFlipBtnTarget) this.flipBtnTarget.classList.add("hidden")
    if (this.hasShareUrlTarget) this.shareUrlTarget.value = ""
  }
}
